import { observer } from "mobx-react";
import AddComments, { AddCommentsRef } from "../../AddComments/AddComments";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import { IRouteParams } from "src/Modules/Company/Interfaces/IRouteParams";
import {
  badgeClasses,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { getFormValidation } from "src/Shared/Components";
import CommentService from "src/Modules/Company/Services/Comment.service";
import { finalize } from "rxjs";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { IResult } from "src/Shared/Interfaces";
import useDebounce from "src/Shared/Hooks/UseDebounce.hook";
import { ICommentFilter } from "src/Modules/Company/Interfaces";
import ViewInputControl from "src/Shared/Components/ViewInputControl/ViewInputControl";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { Divider } from "@mui/material";
import { fields } from "./fields";
import ReviewPriceComment from "../PriceReviewComment/PriceReviewComment";
import { useModal } from "src/Shared/Hooks";
import { CommentType } from "src/Modules/Company/Enums/CommentType.enum";
import { useStyles } from "./SLAComments.styles";

type Props = {
  isSlaRelated: boolean;
  priceReview: boolean;
  onSyncPriceReviewDate?: (priceReview: string | null) => void;
};

const SlaComments: React.FC<Props> = ({ isSlaRelated, priceReview, onSyncPriceReviewDate }, ref) => {
  const {
    rootStore: {
      addCommentStore: {
        commentList,
        getCommentList,
        gridLoading,
        setBtnLoading,
      },
      companyStore: { companyOverview },
    },
  } = useStore();
  const modal = useModal();

  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const [updateCommentList, setUpdateCommentList] = useState<any[]>([]);
  const [search, setSearch] = useState("");

  const classes = useStyles();
  const commentForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => commentForm.$(key);

  useEffect(() => {
    setUpdateCommentList(commentList);
  }, [commentList]);

  useEffect(() => {
    getCommentList(companyRowID, search, isSlaRelated);
  }, [companyRowID]);

  useDebounce(
    () => {
      getCommentList(companyRowID, search, isSlaRelated);
    },
    500,
    [search],
  );

  const onSubmitForm = () => {
    if (!commentForm.isValid) {
      commentForm.showErrors();
      return;
    }

    const request: ICommentFilter = {
      ...commentForm.values(),
      companyRowID,
      commentTypeID: isSlaRelated ? CommentType.SLA : CommentType.Normal,
    };

    setBtnLoading(true);

    CommentService.addComment(request)
      .pipe(
        finalize(() => {
          setBtnLoading(false);
        }),
      )
      .subscribe({
        next: (data: IResult<string>) => {
          commentForm.clear();

          getCommentList(companyRowID, search, isSlaRelated);

          ToastService.success(data.message);
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  const toggleReadMore = (value: number) => {
    const updated = updateCommentList.map((x) => {
      if (x.companyCommentID === value) {
        return {
          ...x,
          isExpanded: !x.isExpanded,
        };
      }
      return x;
    });

    setUpdateCommentList(updated);
  };

  const recordPriceReview = () => {
    modal.open(
      <ReviewPriceComment
        CompanyRowID={companyOverview.companyRowID}
        onSaved={() => getCommentList(companyRowID, search, isSlaRelated)}
        onSyncPriceReviewDate={onSyncPriceReviewDate}
      />,
    );
  };

  return (
    <Box sx={{ height: "81vh", display: "flex", flexDirection: "column" }}>
      <Card elevation={2} className={`${classes.newcommentCard}`}>
        <CardContent>
          <Typography
            sx={{ fontSize: "18px", fontWeight: 700, mb: 3, }}
            color="text.secondary"
          >
            Add Comment
          </Typography>
          <ViewInputControl
            type={EDITOR_TYPES.TEXT_FIELD}
            rows={3}
            form={commentForm}
            field={getField("comment")}
            multiline
          />
          <Box sx={{ marginTop: "-40px" }}>
            <Button onClick={onSubmitForm} variant="contained" color="primary">
              Add Comment
            </Button>
            {priceReview && (
              <Button
                sx={{ ml: 2 }}
                variant="contained"
                onClick={recordPriceReview}
              >
                Record Price Review
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card
        elevation={2}
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1, //takes remaining space
        }}
        className={`${classes.commentHistoryCard}`}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden", //prevents double scroll
          }}
        >
          <Typography
            sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}
            color="text.secondary"
          >
            Comment History
          </Typography>
          <Box
            sx={{
              flex: 1, // takes remaining space
              overflowY: "auto", //  scroll here
            }}
          >
            {updateCommentList?.map((value, index) => (
              <Box key={value.companyCommentID}>
                <Grid
                  container
                  xs={12}
                  sx={{ mt: 1, my: 2 }}
                  className={classes.comments}
                >
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        <span style={{ fontWeight: 600 }}>
                          {value.createdBy}
                        </span>
                        {"   "}
                        {value.createdOn}
                      </Typography>

                      {value.commentTypeID == CommentType.SLA && (
                        <Box
                          className={`${classes.slaComment} ${badgeClasses.colorSuccess}`}
                        >
                          SLA
                        </Box>
                      )}
                      {value.commentTypeID == CommentType.PriceReview && (
                        <Box
                          className={`${classes.slaPriceReview} ${badgeClasses.colorSuccess}`}
                        >
                          Price Review
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1">
                      {value.isExpanded
                        ? value.comment.slice(0, 230)
                        : value.comment}

                      {value.comment.length >= 230 && (
                        <span
                          className={classes.readMore}
                          onClick={() => toggleReadMore(value.companyCommentID)}
                        >
                          {value.isExpanded ? "...Read more" : "...Show less"}
                        </span>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                {index !== updateCommentList.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
export default observer(SlaComments);
